import { DEFAULT_GROQ_CLASSIFY_MODEL } from './llmConfig.ts';

export type QuoteFlowFailureReason =
  | 'classified'
  | 'classification_disabled'
  | 'low_confidence'
  | 'no_keyword_match'
  | 'out_of_range'
  | 'below_document_minimum'
  | 'below_pdf_minimum'
  | 'below_image_minimum'
  | 'below_audio_minimum'
  | 'below_video_minimum'
  | 'classification_failed'
  | 'missing_text'
  | 'unsupported_media'
  | 'ocr_failed'
  | 'analysis_failed'
  | 'unsupported_file'
  | 'pdf_validation_failed'
  | 'manual_review_required';

export type QuoteFlowOutcomePolicy = 'fallback_pricing' | 'custom_quote' | 'reject';

export interface QuoteFlowConfig {
  classification: {
    enabled: boolean;
    model: string;
    minKeywordMatches: number;
    minConfidence: number;
  };
  fallbackPricing: {
    enabled: boolean;
    minWords: number;
    maxWords: number;
    minimumChargedWords: number;
    minimumChargedMinutes: number;
  };
  rejectionThresholds: {
    minPdfWords: number;
    minImageWords: number;
    minDocumentWords: number;
    minAudioMinutes: number;
    minVideoMinutes: number;
  };
  pdfValidation: {
    minWordsPerPage: number;
    maxWordsPerPage: number;
    mixedContentRatio: number;
    sparsePageCountThreshold: number;
    sparseWordsPerPageThreshold: number;
    visuallyHeavyBytesPerPage: number;
    visuallyHeavyWordsPerPageThreshold: number;
  };
  fileTypePolicies: {
    video: 'per_minute';
    audio: 'per_minute';
    unsupported: 'custom_quote' | 'reject';
  };
  uploadExperience: {
    requirePreUploadConsent: boolean;
  };
  outcomePolicies: Record<QuoteFlowFailureReason, QuoteFlowOutcomePolicy>;
}

export interface QuoteFlowSimulationInput {
  fileType: 'text' | 'image' | 'audio' | 'video';
  documentKind?: 'document' | 'pdf';
  wordCount: number;
  pageCount: number;
  durationMinutes: number;
  fileSizeBytes: number;
  hasExtractedText: boolean;
  classificationAttempted: boolean;
  classified: boolean;
  classificationFailureReason?: QuoteFlowFailureReason | '';
  classificationConfidence?: number | null;
  classificationKeywordMatches?: number | null;
  flatFee?: number | null;
}

export interface QuoteFlowSimulationResult {
  decision: 'reject' | 'regular_pricing' | 'classified_pricing' | 'audio_pricing' | 'custom_quote';
  reason: string;
  triggeredRules: string[];
  requiresCustomQuote: boolean;
  shouldReject: boolean;
  shouldUseFallbackPricing: boolean;
}

const QUOTE_FLOW_FAILURE_REASONS: QuoteFlowFailureReason[] = [
  'classified',
  'classification_disabled',
  'low_confidence',
  'no_keyword_match',
  'out_of_range',
  'below_document_minimum',
  'below_pdf_minimum',
  'below_image_minimum',
  'below_audio_minimum',
  'below_video_minimum',
  'classification_failed',
  'missing_text',
  'unsupported_media',
  'ocr_failed',
  'analysis_failed',
  'unsupported_file',
  'pdf_validation_failed',
  'manual_review_required',
];

const DEFAULT_OUTCOME_POLICIES: Record<QuoteFlowFailureReason, QuoteFlowOutcomePolicy> = {
  classified: 'fallback_pricing',
  classification_disabled: 'fallback_pricing',
  low_confidence: 'fallback_pricing',
  no_keyword_match: 'fallback_pricing',
  out_of_range: 'fallback_pricing',
  below_document_minimum: 'fallback_pricing',
  below_pdf_minimum: 'fallback_pricing',
  below_image_minimum: 'fallback_pricing',
  below_audio_minimum: 'fallback_pricing',
  below_video_minimum: 'fallback_pricing',
  classification_failed: 'fallback_pricing',
  missing_text: 'custom_quote',
  unsupported_media: 'custom_quote',
  ocr_failed: 'custom_quote',
  analysis_failed: 'custom_quote',
  unsupported_file: 'custom_quote',
  pdf_validation_failed: 'custom_quote',
  manual_review_required: 'custom_quote',
};

export const DEFAULT_QUOTE_FLOW_CONFIG: QuoteFlowConfig = {
  classification: {
    enabled: true,
    model: DEFAULT_GROQ_CLASSIFY_MODEL,
    minKeywordMatches: 2,
    minConfidence: 0.85,
  },
  fallbackPricing: {
    enabled: true,
    minWords: 25,
    maxWords: 5000,
    minimumChargedWords: 200,
    minimumChargedMinutes: 3,
  },
  rejectionThresholds: {
    minPdfWords: 200,
    minImageWords: 200,
    minDocumentWords: 200,
    minAudioMinutes: 3,
    minVideoMinutes: 3,
  },
  pdfValidation: {
    minWordsPerPage: 200,
    maxWordsPerPage: 600,
    mixedContentRatio: 0.3,
    sparsePageCountThreshold: 4,
    sparseWordsPerPageThreshold: 100,
    visuallyHeavyBytesPerPage: 180000,
    visuallyHeavyWordsPerPageThreshold: 120,
  },
  fileTypePolicies: {
    video: 'per_minute',
    audio: 'per_minute',
    unsupported: 'custom_quote',
  },
  uploadExperience: {
    requirePreUploadConsent: true,
  },
  outcomePolicies: DEFAULT_OUTCOME_POLICIES,
};

const clampNumber = (value: unknown, fallback: number, minimum = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= minimum ? parsed : fallback;
};

export const normalizeQuoteFlowConfig = (
  value: unknown,
  fallbackModel = DEFAULT_GROQ_CLASSIFY_MODEL,
): QuoteFlowConfig => {
  const raw = typeof value === 'string'
    ? (() => {
        try {
          return JSON.parse(value);
        } catch {
          return {};
        }
      })()
    : (typeof value === 'object' && value !== null ? value : {});

  const classification = (raw as any).classification || {};
  const fallbackPricing = (raw as any).fallbackPricing || {};
  const rejectionThresholds = (raw as any).rejectionThresholds || {};
  const pdfValidation = (raw as any).pdfValidation || {};
  const fileTypePolicies = (raw as any).fileTypePolicies || {};
  const uploadExperience = (raw as any).uploadExperience || {};
  const outcomePolicies = (raw as any).outcomePolicies || {};

  return {
    classification: {
      enabled: classification.enabled !== false,
      model: String(classification.model || fallbackModel || DEFAULT_QUOTE_FLOW_CONFIG.classification.model),
      minKeywordMatches: clampNumber(classification.minKeywordMatches, DEFAULT_QUOTE_FLOW_CONFIG.classification.minKeywordMatches),
      minConfidence: clampNumber(classification.minConfidence, DEFAULT_QUOTE_FLOW_CONFIG.classification.minConfidence),
    },
    fallbackPricing: {
      enabled: fallbackPricing.enabled !== false,
      minWords: clampNumber(fallbackPricing.minWords, DEFAULT_QUOTE_FLOW_CONFIG.fallbackPricing.minWords),
      maxWords: clampNumber(
        fallbackPricing.maxWords,
        DEFAULT_QUOTE_FLOW_CONFIG.fallbackPricing.maxWords,
        clampNumber(fallbackPricing.minWords, DEFAULT_QUOTE_FLOW_CONFIG.fallbackPricing.minWords),
      ),
      minimumChargedWords: clampNumber(
        fallbackPricing.minimumChargedWords,
        DEFAULT_QUOTE_FLOW_CONFIG.fallbackPricing.minimumChargedWords,
      ),
      minimumChargedMinutes: clampNumber(
        fallbackPricing.minimumChargedMinutes,
        DEFAULT_QUOTE_FLOW_CONFIG.fallbackPricing.minimumChargedMinutes,
      ),
    },
    rejectionThresholds: {
      minPdfWords: clampNumber(rejectionThresholds.minPdfWords, DEFAULT_QUOTE_FLOW_CONFIG.rejectionThresholds.minPdfWords),
      minImageWords: clampNumber(rejectionThresholds.minImageWords, DEFAULT_QUOTE_FLOW_CONFIG.rejectionThresholds.minImageWords),
      minDocumentWords: clampNumber(rejectionThresholds.minDocumentWords, DEFAULT_QUOTE_FLOW_CONFIG.rejectionThresholds.minDocumentWords),
      minAudioMinutes: clampNumber(rejectionThresholds.minAudioMinutes, DEFAULT_QUOTE_FLOW_CONFIG.rejectionThresholds.minAudioMinutes),
      minVideoMinutes: clampNumber(rejectionThresholds.minVideoMinutes, DEFAULT_QUOTE_FLOW_CONFIG.rejectionThresholds.minVideoMinutes),
    },
    pdfValidation: {
      minWordsPerPage: clampNumber(pdfValidation.minWordsPerPage, DEFAULT_QUOTE_FLOW_CONFIG.pdfValidation.minWordsPerPage),
      maxWordsPerPage: clampNumber(pdfValidation.maxWordsPerPage, DEFAULT_QUOTE_FLOW_CONFIG.pdfValidation.maxWordsPerPage),
      mixedContentRatio: clampNumber(pdfValidation.mixedContentRatio, DEFAULT_QUOTE_FLOW_CONFIG.pdfValidation.mixedContentRatio),
      sparsePageCountThreshold: clampNumber(pdfValidation.sparsePageCountThreshold, DEFAULT_QUOTE_FLOW_CONFIG.pdfValidation.sparsePageCountThreshold),
      sparseWordsPerPageThreshold: clampNumber(pdfValidation.sparseWordsPerPageThreshold, DEFAULT_QUOTE_FLOW_CONFIG.pdfValidation.sparseWordsPerPageThreshold),
      visuallyHeavyBytesPerPage: clampNumber(pdfValidation.visuallyHeavyBytesPerPage, DEFAULT_QUOTE_FLOW_CONFIG.pdfValidation.visuallyHeavyBytesPerPage),
      visuallyHeavyWordsPerPageThreshold: clampNumber(pdfValidation.visuallyHeavyWordsPerPageThreshold, DEFAULT_QUOTE_FLOW_CONFIG.pdfValidation.visuallyHeavyWordsPerPageThreshold),
    },
    fileTypePolicies: {
      video: 'per_minute',
      audio: 'per_minute',
      unsupported: fileTypePolicies.unsupported === 'reject' ? 'reject' : 'custom_quote',
    },
    uploadExperience: {
      requirePreUploadConsent: uploadExperience.requirePreUploadConsent !== false,
    },
    outcomePolicies: {
      ...DEFAULT_OUTCOME_POLICIES,
      ...Object.fromEntries(
        Object.entries(outcomePolicies).filter(([, policy]) =>
          policy === 'fallback_pricing' || policy === 'custom_quote' || policy === 'reject'
        ),
      ) as Record<QuoteFlowFailureReason, QuoteFlowOutcomePolicy>,
    },
  };
};

export const serializeQuoteFlowConfig = (config: QuoteFlowConfig) =>
  JSON.stringify(normalizeQuoteFlowConfig(config, config.classification.model));

export const isWithinFallbackPricingRange = (wordCount: number, config: QuoteFlowConfig) =>
  config.fallbackPricing.enabled &&
  Number.isFinite(wordCount) &&
  wordCount >= config.fallbackPricing.minWords &&
  wordCount <= config.fallbackPricing.maxWords;

export const isWithinFallbackMinuteRange = (durationMinutes: number, config: QuoteFlowConfig) =>
  config.fallbackPricing.enabled &&
  Number.isFinite(durationMinutes) &&
  durationMinutes > 0;

type LowContentFileKind = 'document' | 'pdf' | 'image' | 'audio' | 'video';

const LOW_CONTENT_DURATION_REASONS: QuoteFlowFailureReason[] = [
  'below_audio_minimum',
  'below_video_minimum',
];

export const getLowContentRejection = (
  fileType: LowContentFileKind,
  metrics: { wordCount?: number | null; durationMinutes?: number | null },
  config: QuoteFlowConfig,
) => {
  const wordCount = Number(metrics.wordCount || 0);
  const durationMinutes = Number(metrics.durationMinutes || 0);

  let reason: QuoteFlowFailureReason | null = null;
  let threshold = 0;

  if (fileType === 'image' && wordCount > 0 && wordCount < config.rejectionThresholds.minImageWords) {
    reason = 'below_image_minimum';
    threshold = config.rejectionThresholds.minImageWords;
  } else if (fileType === 'document' && wordCount > 0 && wordCount < config.rejectionThresholds.minDocumentWords) {
    reason = 'below_document_minimum';
    threshold = config.rejectionThresholds.minDocumentWords;
  } else if (fileType === 'pdf' && wordCount > 0 && wordCount < config.rejectionThresholds.minPdfWords) {
    reason = 'below_pdf_minimum';
    threshold = config.rejectionThresholds.minPdfWords;
  } else if (fileType === 'audio' && durationMinutes > 0 && durationMinutes < config.rejectionThresholds.minAudioMinutes) {
    reason = 'below_audio_minimum';
    threshold = config.rejectionThresholds.minAudioMinutes;
  } else if (fileType === 'video' && durationMinutes > 0 && durationMinutes < config.rejectionThresholds.minVideoMinutes) {
    reason = 'below_video_minimum';
    threshold = config.rejectionThresholds.minVideoMinutes;
  }

  if (!reason) {
    return {
      matched: false as const,
      shouldReject: false as const,
      requiresCustomQuote: false as const,
      shouldUseFallbackPricing: false as const,
      decision: null,
      reason: null,
      threshold: null,
    };
  }

  const policy = config.outcomePolicies[reason] || 'custom_quote';
  const canFallback = LOW_CONTENT_DURATION_REASONS.includes(reason)
    ? isWithinFallbackMinuteRange(durationMinutes, config)
    : wordCount > 0;

  if (policy === 'reject') {
    return {
      matched: true as const,
      shouldReject: true as const,
      requiresCustomQuote: false as const,
      shouldUseFallbackPricing: false as const,
      decision: 'reject' as const,
      reason,
      threshold,
    };
  }

  if (policy === 'fallback_pricing' && canFallback) {
    return {
      matched: true as const,
      shouldReject: false as const,
      requiresCustomQuote: false as const,
      shouldUseFallbackPricing: true as const,
      decision: LOW_CONTENT_DURATION_REASONS.includes(reason) ? 'audio_pricing' as const : 'regular_pricing' as const,
      reason,
      threshold,
    };
  }

  return {
    matched: true as const,
    shouldReject: false as const,
    requiresCustomQuote: true as const,
    shouldUseFallbackPricing: false as const,
    decision: 'custom_quote' as const,
    reason,
    threshold,
  };
};

export const validatePdfWordCount = (
  wordCount: number,
  pageCount: number,
  fileSize: number | undefined,
  config: QuoteFlowConfig,
) => {
  if (pageCount <= 0) {
    return { isValid: true, reason: undefined, triggeredRule: null as string | null };
  }

  if (wordCount === 0) {
    return {
      isValid: false,
      reason: 'No text detected; likely scanned or image-based document.',
      triggeredRule: 'pdf:no_text_detected',
    };
  }

  if (wordCount <= 50) {
    return { isValid: true, reason: undefined, triggeredRule: null as string | null };
  }

  const wordsPerPage = wordCount / pageCount;
  const bytesPerPage = fileSize && pageCount > 0 ? fileSize / pageCount : 0;
  const minWordsPerPageThreshold =
    config.pdfValidation.minWordsPerPage * config.pdfValidation.mixedContentRatio;

  if (
    pageCount >= config.pdfValidation.sparsePageCountThreshold &&
    wordsPerPage < config.pdfValidation.sparseWordsPerPageThreshold
  ) {
    return {
      isValid: false,
      reason: `Sparse text spread across ${pageCount} pages (${Math.round(wordsPerPage)} words/page). This likely needs manual review.`,
      triggeredRule: 'pdf:sparse_text_pages',
    };
  }

  if (
    bytesPerPage > config.pdfValidation.visuallyHeavyBytesPerPage &&
    wordsPerPage < config.pdfValidation.visuallyHeavyWordsPerPageThreshold
  ) {
    return {
      isValid: false,
      reason: `Low text density with visually heavy pages (${Math.round(wordsPerPage)} words/page). This likely contains significant image-based content.`,
      triggeredRule: 'pdf:visually_heavy_low_density',
    };
  }

  if (wordsPerPage < minWordsPerPageThreshold) {
    return {
      isValid: false,
      reason: `Low word density (${Math.round(wordsPerPage)} words/page). Likely scanned or mixed-content document.`,
      triggeredRule: 'pdf:low_word_density',
    };
  }

  return { isValid: true, reason: undefined, triggeredRule: null as string | null };
};

export const shouldAttemptClassification = (
  config: QuoteFlowConfig,
  input: { fileType: 'text' | 'image' | 'audio' | 'video'; wordCount?: number | null; extractedText?: string | null },
) => {
  if (!config.classification.enabled) return false;
  if (input.fileType === 'audio' || input.fileType === 'video') return false;
  const text = String(input.extractedText || '').trim();
  return Boolean(text) || Number(input.wordCount || 0) > 0;
};

export const resolveQuoteFlowOutcome = (
  reason: unknown,
  input: { fileType: 'text' | 'image' | 'audio' | 'video'; wordCount?: number | null; durationMinutes?: number | null; hasUsableText?: boolean },
  config: QuoteFlowConfig,
) => {
  const reasonCandidate = typeof reason === 'string' && reason.trim().length > 0
    ? reason
    : 'classification_failed';
  const resolvedReason = QUOTE_FLOW_FAILURE_REASONS.includes(reasonCandidate as QuoteFlowFailureReason)
    ? reasonCandidate as QuoteFlowFailureReason
    : 'classification_failed';
  const policy = config.outcomePolicies[resolvedReason] || 'custom_quote';
  const hasUsableText = Boolean(input.hasUsableText) && Number(input.wordCount || 0) > 0;
  const isLowDurationReason = LOW_CONTENT_DURATION_REASONS.includes(resolvedReason);
  const isLowContentReason = resolvedReason.startsWith('below_');
  const canFallback = isLowContentReason
    ? (
      isLowDurationReason
        ? Number(input.durationMinutes || 0) > 0
        : Number(input.wordCount || 0) > 0
    )
    : resolvedReason === 'pdf_validation_failed'
      ? hasUsableText
      : hasUsableText && isWithinFallbackPricingRange(Number(input.wordCount || 0), config);

  if (policy === 'reject') {
    return {
      action: 'reject' as const,
      requiresCustomQuote: false,
      shouldReject: true,
      shouldUseFallbackPricing: false,
      reason: resolvedReason,
    };
  }

  if (policy === 'fallback_pricing' && canFallback) {
    return {
      action: input.fileType === 'audio' || input.fileType === 'video' ? 'audio_pricing' as const : 'regular_pricing' as const,
      requiresCustomQuote: false,
      shouldReject: false,
      shouldUseFallbackPricing: true,
      reason: resolvedReason,
    };
  }

  return {
    action: 'custom_quote' as const,
    requiresCustomQuote: true,
    shouldReject: false,
    shouldUseFallbackPricing: false,
    reason: resolvedReason,
  };
};

export const simulateQuoteFlow = (
  rawConfig: unknown,
  input: QuoteFlowSimulationInput,
): QuoteFlowSimulationResult => {
  const config = normalizeQuoteFlowConfig(rawConfig, typeof rawConfig === 'object' && rawConfig !== null ? (rawConfig as any)?.classification?.model : undefined);
  const triggeredRules: string[] = [];

  const lowContent = getLowContentRejection(
    input.fileType === 'text'
      ? (input.documentKind === 'pdf' ? 'pdf' : 'document')
      : input.fileType,
    {
    wordCount: input.wordCount,
    durationMinutes: input.durationMinutes,
  },
    config,
  );

  const shouldApplyEarlyLowContentOutcome =
    lowContent.matched &&
    (input.fileType === 'audio' || input.fileType === 'video');

  if (shouldApplyEarlyLowContentOutcome) {
    triggeredRules.push(`${lowContent.decision}:${lowContent.reason}`);
    return {
      decision: lowContent.decision,
      reason: lowContent.reason || 'threshold_outcome',
      triggeredRules,
      requiresCustomQuote: lowContent.requiresCustomQuote,
      shouldReject: lowContent.shouldReject,
      shouldUseFallbackPricing: lowContent.shouldUseFallbackPricing,
    };
  }

  if (input.fileType === 'audio' || input.fileType === 'video') {
    triggeredRules.push('file_type:audio_per_minute');
    return {
      decision: 'audio_pricing',
      reason: 'duration_based_pricing',
      triggeredRules,
      requiresCustomQuote: false,
      shouldReject: false,
      shouldUseFallbackPricing: false,
    };
  }

  if (input.fileType === 'text' && input.documentKind === 'pdf' && input.pageCount > 0) {
    const pdfValidation = validatePdfWordCount(input.wordCount, input.pageCount, input.fileSizeBytes, config);
    if (!pdfValidation.isValid) {
      const outcome = resolveQuoteFlowOutcome('pdf_validation_failed', {
        fileType: 'text',
        wordCount: input.wordCount,
        hasUsableText: input.hasExtractedText,
      }, config);
      triggeredRules.push(pdfValidation.triggeredRule || 'pdf:invalid');
      triggeredRules.push(`outcome:${outcome.reason}`);
      return {
        decision: outcome.action === 'regular_pricing'
          ? 'regular_pricing'
          : outcome.action === 'reject'
            ? 'reject'
            : 'custom_quote',
        reason: pdfValidation.reason || 'pdf_validation_failed',
        triggeredRules,
        requiresCustomQuote: outcome.requiresCustomQuote,
        shouldReject: outcome.shouldReject,
        shouldUseFallbackPricing: outcome.shouldUseFallbackPricing,
      };
    }
  }

  if (!shouldAttemptClassification(config, {
    fileType: input.fileType,
    wordCount: input.wordCount,
    extractedText: input.hasExtractedText ? 'text' : '',
  })) {
    triggeredRules.push(config.classification.enabled ? 'classification:not_attempted' : 'classification:disabled');

    if (!config.classification.enabled && isWithinFallbackPricingRange(input.wordCount, config)) {
      return {
        decision: 'regular_pricing',
        reason: 'classification_disabled',
        triggeredRules,
        requiresCustomQuote: false,
        shouldReject: false,
        shouldUseFallbackPricing: true,
      };
    }
  }

  if (input.classified && Number(input.flatFee || 0) > 0) {
    triggeredRules.push('classification:classified_flat_fee');
    return {
      decision: 'classified_pricing',
      reason: 'classified',
      triggeredRules,
      requiresCustomQuote: false,
      shouldReject: false,
      shouldUseFallbackPricing: false,
    };
  }

  if (lowContent.matched) {
    triggeredRules.push(`${lowContent.decision}:${lowContent.reason}`);
    return {
      decision: lowContent.decision,
      reason: lowContent.reason || 'threshold_outcome',
      triggeredRules,
      requiresCustomQuote: lowContent.requiresCustomQuote,
      shouldReject: lowContent.shouldReject,
      shouldUseFallbackPricing: lowContent.shouldUseFallbackPricing,
    };
  }

  const outcome = resolveQuoteFlowOutcome(
    input.classificationFailureReason || (config.classification.enabled ? 'classification_failed' : 'classification_disabled'),
    {
      fileType: input.fileType,
      wordCount: input.wordCount,
      hasUsableText: input.hasExtractedText,
    },
    config,
  );

  triggeredRules.push(`outcome:${outcome.reason}`);

  return {
    decision: outcome.action === 'audio_pricing'
      ? 'audio_pricing'
      : outcome.action === 'regular_pricing'
        ? 'regular_pricing'
        : outcome.action === 'reject'
          ? 'reject'
          : 'custom_quote',
    reason: outcome.reason,
    triggeredRules,
    requiresCustomQuote: outcome.requiresCustomQuote,
    shouldReject: outcome.shouldReject,
    shouldUseFallbackPricing: outcome.shouldUseFallbackPricing,
  };
};
